// ============================================================
// Landing Page — Public homepage for unauthenticated users
// ============================================================

import { Link } from 'react-router-dom';
import { Briefcase, FileText, Brain, BarChart3, Shield, Zap, ArrowRight } from 'lucide-react';
import { PublicFooter } from './PublicPages';

export function LandingPage() {
    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--color-bg-primary)' }}>
            {/* Nav */}
            <nav style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '20px 40px', borderBottom: '1px solid var(--color-border)',
                position: 'sticky', top: 0, background: 'var(--color-bg-primary)',
                zIndex: 100, backdropFilter: 'blur(12px)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontWeight: 800, fontSize: 18,
                        }}>H</div>
                        <span style={{
                            fontSize: '1.25rem', fontWeight: 800,
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        }}>HireHub</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Link to="/features" className="btn btn-ghost" style={{ fontSize: 14 }}>Features</Link>
                        <Link to="/about" className="btn btn-ghost" style={{ fontSize: 14 }}>About</Link>
                        <Link to="/contact" className="btn btn-ghost" style={{ fontSize: 14 }}>Contact</Link>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <Link to="/login" className="btn btn-ghost">Sign In</Link>
                    <Link to="/signup" className="btn btn-primary">Get Started</Link>
                </div>
            </nav>

            {/* Hero */}
            <section style={{
                textAlign: 'center', padding: '80px 40px 60px',
                maxWidth: 800, margin: '0 auto',
            }}>
                <div style={{
                    display: 'inline-block', padding: '6px 16px', borderRadius: 20,
                    background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1',
                    fontSize: '13px', fontWeight: 600, marginBottom: 24,
                }}>
                    AI-Powered Hiring Automation
                </div>
                <h1 style={{
                    fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800,
                    lineHeight: 1.2, marginBottom: 20,
                }}>
                    Hire smarter,{' '}
                    <span style={{
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a78bfa)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    }}>faster</span>
                </h1>
                <p style={{
                    fontSize: '1.1rem', color: '#4b5563', lineHeight: 1.8,
                    maxWidth: 560, margin: '0 auto 40px',
                }}>
                    From job postings to AI-scored assessments — manage your entire hiring
                    pipeline in one place. No spreadsheets, no guesswork.
                </p>
                <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                    <Link to="/signup" className="btn btn-primary btn-lg">
                        Start for Free <ArrowRight size={18} />
                    </Link>
                    <Link to="/login" className="btn btn-secondary btn-lg">
                        Sign In
                    </Link>
                </div>
            </section>

            {/* Features Grid */}
            <section style={{
                maxWidth: 1000, margin: '0 auto', padding: '40px 40px 80px',
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 24, flex: 1,
            }}>
                <FeatureCard icon={<Briefcase size={24} />} title="Job Pipeline"
                    description="Custom stages, drag-and-drop pipeline, and candidate tracking for every role" />
                <FeatureCard icon={<FileText size={24} />} title="Form Builder"
                    description="Drag-and-drop application forms with 12+ field types including table grids" />
                <FeatureCard icon={<Brain size={24} />} title="AI Scoring"
                    description="Automated assessment grading with detailed rubric-based explanations" />
                <FeatureCard icon={<BarChart3 size={24} />} title="Leaderboards"
                    description="Ranked candidate views with composite scores and CSV exports" />
                <FeatureCard icon={<Shield size={24} />} title="Secure Assessments"
                    description="Timed tests with OTP verification, shuffled questions, and auto-save" />
                <FeatureCard icon={<Zap size={24} />} title="Question Bank"
                    description="Versioned MCQ and subjective questions with difficulty tagging" />
            </section>

            {/* Footer */}
            <PublicFooter />
        </div>
    );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
    return (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {icon}
            </div>
            <h3 style={{ fontWeight: 700, fontSize: '15px' }}>{title}</h3>
            <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: 1.6 }}>{description}</p>
        </div>
    );
}
